import { makeAuthenticatedApiCall } from '../../../apiHelper';
import type { QontoInvoiceItem } from './invoiceItemsMath';
import type { SavedInvoiceTitle } from './InvoiceItemsEditor';

/** Facture complémentaire d'un dossier CG, telle que renvoyée par l'API. */
export type ComplementMotif = 'ticket_ants' | 'difference_taxes' | 'autre';
export type ComplementStatut = 'propose' | 'accepte' | 'paye' | 'annule';

export interface Complement {
  id: number;
  registrationId: number;
  motif: ComplementMotif;
  titre: string;
  texte: string | null;
  items: QontoInvoiceItem[];
  montantHtCentimes: number;
  montantTtcCentimes: number;
  statut: ComplementStatut;
  modeFacturation: 'dossier' | 'releve' | null;
  facture: { numero: string; statut: string | null; url: string | null } | null;
  proposeLe: string;
  accepteLe: string | null;
  virementSignaleLe: string | null;
  payeLe: string | null;
  iban: string | null;
}

export const MOTIF_OPTIONS: Array<{ value: ComplementMotif; label: string }> = [
  { value: 'ticket_ants', label: 'Ticket ANTS' },
  { value: 'difference_taxes', label: 'Différence de taxes' },
  { value: 'autre', label: 'Autre' },
];

export const euros = (centimes: number): string =>
  (centimes / 100).toLocaleString('fr-FR', { style: 'currency', currency: 'EUR' });

type Reponse<T> = { success: boolean; data: T; error?: string; warning?: string };

export const listComplements = async (registrationId: number): Promise<Complement[]> => {
  const { data } = await makeAuthenticatedApiCall<Reponse<Complement[]>>(
    'get',
    `/api/cg/${registrationId}/complements`,
  );
  return data.data;
};

export const proposeComplement = async (
  registrationId: number,
  payload: { motif: ComplementMotif; texte: string; items: QontoInvoiceItem[] },
): Promise<Complement> => {
  const { data } = await makeAuthenticatedApiCall<Reponse<Complement>>(
    'post',
    `/api/cg/${registrationId}/complements`,
    payload,
  );
  return data.data;
};

export const acceptComplement = async (id: number): Promise<Reponse<Complement>> => {
  const { data } = await makeAuthenticatedApiCall<Reponse<Complement>>('post', `/api/cg/complements/${id}/accept`);
  return data;
};

export const cancelComplement = async (id: number): Promise<void> => {
  await makeAuthenticatedApiCall('post', `/api/cg/complements/${id}/cancel`);
};

export const reportComplementTransfer = async (id: number): Promise<Complement> => {
  const { data } = await makeAuthenticatedApiCall<Reponse<Complement>>(
    'post',
    `/api/cg/complements/${id}/transfer-reported`,
  );
  return data.data;
};

type TemplateApi = {
  title: string;
  description: string | null;
  unit: string | null;
  unit_price_ht: string | null;
  vat_rate: string | null;
};

const versTitre = (t: TemplateApi): SavedInvoiceTitle => ({
  title: t.title,
  description: t.description ?? '',
  unit: t.unit ?? undefined,
  unitPriceHT: t.unit_price_ht ?? undefined,
  vatRate: t.vat_rate ?? undefined,
});

export const listSavedTitles = async (): Promise<SavedInvoiceTitle[]> => {
  const { data } = await makeAuthenticatedApiCall<Reponse<TemplateApi[]>>('get', '/api/cg/invoice-item-templates');
  return data.data.map(versTitre);
};

export const saveTitle = async (saved: SavedInvoiceTitle): Promise<SavedInvoiceTitle[]> => {
  const { data } = await makeAuthenticatedApiCall<Reponse<TemplateApi[]>>('post', '/api/cg/invoice-item-templates', {
    title: saved.title,
    description: saved.description,
    unit: saved.unit,
    unitPriceHT: saved.unitPriceHT ? Number(String(saved.unitPriceHT).replace(',', '.')) : null,
    vatRate: saved.vatRate,
  });
  return data.data.map(versTitre);
};

/** Message d'erreur lisible depuis une erreur axios. */
export const messageErreur = (error: unknown, defaut: string): string =>
  (error as { response?: { data?: { error?: string; message?: string } } })?.response?.data?.error ??
  (error as { response?: { data?: { message?: string } } })?.response?.data?.message ??
  defaut;
